/**
 * Driver roles and the EMS role / level choice (feature flag `driver_roles`, default off). Pure: no
 * Firebase, no React, so scripts/syncFunctionsGenerated.mjs copies it to functions/src/generated/ and
 * the employer's view of an applicant's match (functions/src/candidateEvaluationForEmployer.ts) runs
 * exactly the code the candidate's own pages run.
 *
 * Why: ambulance companies (MedEx Medical Transport, 2026-10-09) hire Ambulance Drivers and Stretcher
 * Van Drivers. Those are real jobs that need no EMS certification. Rules, all behind the flag:
 *
 *  - A job declares its role category in `roleCategory` ('driver' | 'ems'). Absent = 'ems', so every
 *    existing job reads exactly as before. A driver pack (Ambulance Driver, Stretcher Van Driver) or
 *    the select on Post a Job sets it. Nothing here writes a job.
 *  - A driver job's match is computed from the job's OWN requirements only (an EMS certification is
 *    never required unless the employer listed one) and from driver documents (driver's license,
 *    driving record, CPR/BLS card) in place of the EMS readiness score, whose Credentials and
 *    Documentation parts are about NREMT, state EMS licenses and EMS skills.
 *  - A person picks a role / level in sign-up and on the profile (professional.roleLevel): EMR,
 *    EMT-Basic, AEMT, Paramedic, Dispatcher, or Driver / non-certified. A driver is offered driver
 *    credentials, sees a readiness view about driving, and EMS-certified jobs are hidden from them by
 *    default.
 *  - Dispatcher (2026-11-01, Trident): the same idea for dispatch work. A 'dispatch' job needs no EMS
 *    certification unless the employer listed one, and is matched on dispatch background (the role
 *    picked on the profile, or a past job titled dispatcher), experience and profile basics. An EMS
 *    card still counts when the employer asked for it, and a dispatcher who holds one keeps it.
 *
 * Flag off: every function below answers as if the flag did not exist (isDriverJob false,
 * isDriverProfile false), so callers keep today's behaviour exactly.
 */
import { isEmployerArrangedItem } from '../requirementPacks';
import {
  CERT_CATEGORY,
  CA_AMBULANCE_DRIVER_CERTIFICATE,
  resolveCanonicalCertification,
  type Certification,
} from '../constants/certifications';
import { getJobCertRequirements } from '../certificationMatching';
import { isCredentialDocumentCategory } from '../documents/documentCategories';
import { resolveCertVerification, resolveDocumentVerification } from '../credentials/certificationChecks';
import { isDocumentReviewed } from '../credentials/documentReviewGate';
import { isExpired, scoringClock } from '../readiness/certUtils';
import { documentCardExpiryDay } from '../renewal/credentialExpiry';

export const DRIVER_ROLES_FLAG = 'driver_roles';
export const DRIVER_ROLES_FLAG_LABEL = 'Driver roles and EMS level choice';
export const DRIVER_ROLES_FLAG_DESCRIPTION =
  'Employers can post driver jobs (Ambulance Driver, Stretcher Van Driver) that need no EMS certification, and candidates pick a role or level (EMR, EMT-Basic, AEMT, Paramedic or Driver / non-certified). Driver jobs are matched on driver requirements only; drivers see driver credentials and do not see EMS-certified jobs by default. Off: today\'s behaviour.';

/* ------------------------------------------------------------------ the person's role / level */

export const ROLE_LEVELS = ['EMR', 'EMT-B', 'AEMT', 'Paramedic', 'dispatcher', 'driver'] as const;
export type RoleLevel = (typeof ROLE_LEVELS)[number];

export const ROLE_LEVEL_LABELS: Record<RoleLevel, string> = {
  EMR: 'EMR (Emergency Medical Responder)',
  'EMT-B': 'EMT-Basic',
  AEMT: 'AEMT (Advanced EMT)',
  Paramedic: 'Paramedic',
  dispatcher: 'Dispatcher',
  driver: 'Driver / non-certified',
};

/** A stored or typed role / level, or null when it is not one of ROLE_LEVELS. */
export function normalizeRoleLevel(raw: unknown): RoleLevel | null {
  if (typeof raw !== 'string') return null;
  const t = raw.trim();
  if (!t) return null;
  const direct = ROLE_LEVELS.find((l) => l.toLowerCase() === t.toLowerCase());
  if (direct) return direct;
  if (/^(driver|non[- ]?certified)$/i.test(t)) return 'driver';
  if (/^dispatch(er)?$/i.test(t)) return 'dispatcher';
  if (/^emt([- ]?basic)?$/i.test(t)) return 'EMT-B';
  return null;
}

/** professional.roleLevel on a profile (owner-written), normalized. */
export function profileRoleLevel(profile: unknown): RoleLevel | null {
  const pro = profile && typeof profile === 'object' ? (profile as { professional?: { roleLevel?: unknown } | null }).professional : null;
  return normalizeRoleLevel(pro?.roleLevel);
}

/** True only while the flag is on AND the person chose "Driver / non-certified". */
export function isDriverProfile(profile: unknown, driverRoles: boolean): boolean {
  return driverRoles === true && profileRoleLevel(profile) === 'driver';
}

/** True only while the flag is on AND the person chose "Dispatcher". */
export function isDispatcherProfile(profile: unknown, driverRoles: boolean): boolean {
  return driverRoles === true && profileRoleLevel(profile) === 'dispatcher';
}

/** Driver or Dispatcher: EMS certifications are not asked of them and EMS-only jobs stay out of the way. */
export function isNonEmsProfile(profile: unknown, driverRoles: boolean): boolean {
  return isDriverProfile(profile, driverRoles) || isDispatcherProfile(profile, driverRoles);
}

/**
 * Has this person done dispatch work? The role they picked (`roleLevel` on publicProfiles,
 * `professional.roleLevel` on a profile) or a past job titled dispatcher. Reads either document.
 */
export function hasDispatchBackground(profile: unknown): boolean {
  if (!profile || typeof profile !== 'object') return false;
  const p = profile as { roleLevel?: unknown; pastExperience?: unknown; experienceSummaryDetailed?: unknown; professional?: { pastExperience?: unknown } | null };
  if (normalizeRoleLevel(p.roleLevel) === 'dispatcher' || profileRoleLevel(profile) === 'dispatcher') return true;
  const past = [p.pastExperience, p.experienceSummaryDetailed, p.professional?.pastExperience].flatMap((l) => (Array.isArray(l) ? l : [])) as Array<{ roleTitle?: unknown } | null>;
  return past.some((e) => typeof e?.roleTitle === 'string' && /dispatch/i.test(e.roleTitle));
}

/* ------------------------------------------------------------------ the job's role category */

export const JOB_ROLE_CATEGORIES = ['ems', 'driver', 'dispatch'] as const;
export type JobRoleCategory = (typeof JOB_ROLE_CATEGORIES)[number];

export const JOB_ROLE_CATEGORY_LABELS: Record<JobRoleCategory, string> = {
  ems: 'EMS role (EMT, AEMT, Paramedic and similar)',
  driver: 'Driver role (no EMS certification needed)',
  dispatch: 'Dispatcher role (no EMS certification needed)',
};

export function normalizeJobRoleCategory(raw: unknown): JobRoleCategory | null {
  return raw === 'driver' || raw === 'ems' || raw === 'dispatch' ? raw : null;
}

/** The job's role category; absent or unknown is 'ems' (every job posted before 2026-10-09). */
export function jobRoleCategory(job: unknown): JobRoleCategory {
  const raw = job && typeof job === 'object' ? (job as { roleCategory?: unknown }).roleCategory : null;
  return normalizeJobRoleCategory(raw) ?? 'ems';
}

/** A driver job, for matching purposes: only while the flag is on. */
export function isDriverJob(job: unknown, driverRoles: boolean): boolean {
  return driverRoles === true && jobRoleCategory(job) === 'driver';
}

/** A dispatcher job, for matching purposes: only while the flag is on. */
export function isDispatchJob(job: unknown, driverRoles: boolean): boolean {
  return driverRoles === true && jobRoleCategory(job) === 'dispatch';
}

/* ------------------------------------------------------------------ credentials */

/**
 * An EMS certification: National Registry registration, a training level (EMR, EMT-B, AEMT,
 * Paramedic) or a State EMS Certification. Skills cards (BLS, ACLS, PALS) and driving credentials
 * (EVOC, California Ambulance Driver Certificate) are not.
 */
export function isEmsCertification(name: string): boolean {
  const canonical = resolveCanonicalCertification(String(name ?? ''));
  if (!canonical) return false;
  const category = CERT_CATEGORY[canonical];
  return category === 'registration' || category === 'training_level' || category === 'state_certification';
}

/**
 * Credentials the sign-up credentials step and the profile offer a driver: CPR/BLS and the driving
 * credentials. (The driver's license itself is a document, uploaded on the profile.)
 */
export const DRIVER_CREDENTIALS: readonly Certification[] = ['BLS', 'EVOC', CA_AMBULANCE_DRIVER_CERTIFICATE];

export function isDriverCredential(name: string): boolean {
  const canonical = resolveCanonicalCertification(String(name ?? ''));
  return Boolean(canonical && DRIVER_CREDENTIALS.includes(canonical));
}

/** The job's REQUIRED certifications that are EMS certifications (display order kept). */
export function jobRequiredEmsCertifications(job: unknown): string[] {
  const { required } = getJobCertRequirements((job ?? {}) as Parameters<typeof getJobCertRequirements>[0]);
  return required.filter((r) => isEmsCertification(r));
}

/* ------------------------------------------------------------------ driver documents */

/** Credentialing item keys a driver job can ask for, and the profile document that shows each one. */
export const DRIVER_ITEM_DOCUMENT_CATEGORY: Readonly<Record<string, string>> = {
  drivers_license: 'driversLicense',
  dmv_record: 'drivingRecord',
  bls: 'cprBls',
};

export const DRIVER_ITEM_LABELS: Readonly<Record<string, string>> = {
  drivers_license: "Driver's license",
  dmv_record: 'Driving record (MVR)',
  bls: 'CPR / BLS card',
};

/**
 * The driver items this job lists in credentialingRequirements.requiredItems (known keys only) that the
 * candidate brings. The driving record (MVR) is arranged by the employer (B2, 2026-10-10), so it is
 * never scored, even on jobs that list it.
 */
export function jobDriverItems(job: unknown): string[] {
  const cr = job && typeof job === 'object'
    ? (job as { credentialingRequirements?: { requiredItems?: unknown } | null }).credentialingRequirements
    : null;
  const items = Array.isArray(cr?.requiredItems) ? cr!.requiredItems as unknown[] : [];
  return [...new Set(items.filter((i): i is string => typeof i === 'string' && i in DRIVER_ITEM_DOCUMENT_CATEGORY && !isEmployerArrangedItem(i)))];
}

type DocProfile = {
  profileDocuments?: Array<{ category?: string; downloadUrl?: string; hasFile?: boolean } & Record<string, unknown>> | null;
  certificationChecks?: unknown;
  professional?: { certifications?: unknown } | null;
};

/**
 * Is this driver item on the profile? Same rule calculateReadiness uses for documents: a credential
 * document (driver's license, CPR/BLS card) counts only once it passed the document review; a
 * compliance document (driving record) counts once uploaded. A CPR/BLS item is also met by a BLS
 * certification whose document passed review.
 */
export function profileHasDriverItem(profile: unknown, item: string, nowIn: Date = new Date()): boolean {
  const p = (profile && typeof profile === 'object' ? profile : {}) as DocProfile;
  const category = DRIVER_ITEM_DOCUMENT_CATEGORY[item];
  if (!category) return false;
  // The counting rule (2026-10-13): reviewed AND current, on the EMT's own calendar. An expired
  // driver's license or CPR card used to keep counting here.
  const now = scoringClock(profile, nowIn);
  const docs = Array.isArray(p.profileDocuments) ? p.profileDocuments : [];
  const docOnFile = docs.some((d) => {
    if (!d || d.category !== category || !(d.downloadUrl || d.hasFile === true)) return false;
    if (!isCredentialDocumentCategory(category)) return true;
    if (!isDocumentReviewed(resolveDocumentVerification(d, p.certificationChecks))) return false;
    const exp = (typeof d.expiresAt === 'string' && d.expiresAt) || documentCardExpiryDay(d, p.certificationChecks) || undefined;
    return !isExpired(exp, now);
  });
  if (docOnFile) return true;
  if (item === 'bls') {
    const certs = Array.isArray(p.professional?.certifications) ? (p.professional!.certifications as unknown[]) : [];
    return certs.some((c) => {
      if (!c || typeof c !== 'object') return false;
      if (resolveCanonicalCertification(String((c as { name?: unknown }).name ?? '')) !== 'BLS') return false;
      if (!isDocumentReviewed(resolveCertVerification(c, p.certificationChecks))) return false;
      const raw = c as { expiryDate?: unknown; expiresAt?: unknown };
      const exp = (typeof raw.expiryDate === 'string' && raw.expiryDate) || (typeof raw.expiresAt === 'string' && raw.expiresAt) || undefined;
      return !isExpired(exp, now);
    });
  }
  return false;
}

export type DriverJobFloor = {
  /** 0-100: what replaces the EMS readiness score in a driver job's match. */
  score: number;
  /** Driver items the job lists (drivers_license, dmv_record, bls). */
  itemsRequired: string[];
  /** Of those, the ones on the profile. */
  itemsOnFile: string[];
};

/**
 * The part of a driver job's match that the EMS readiness score fills for an EMS job. Built from what
 * a driver job asks for, never from NREMT / state license / EMS skills:
 *  - driver documents the job lists (weight 20), when it lists any;
 *  - experience on the profile (readiness Experience part, weight 20);
 *  - profile basics (readiness Profile part, weight 5).
 */
export function driverJobFloor(
  profile: unknown,
  job: unknown,
  readinessPillars: { experience: { score: number }; profileActivity: { score: number } },
  now: Date = new Date(),
): DriverJobFloor {
  const itemsRequired = jobDriverItems(job);
  const itemsOnFile = itemsRequired.filter((i) => profileHasDriverItem(profile, i, now));
  const parts: Array<{ score: number; weight: number }> = [
    ...(itemsRequired.length > 0 ? [{ score: Math.round((100 * itemsOnFile.length) / itemsRequired.length), weight: 20 }] : []),
    { score: readinessPillars.experience.score, weight: 20 },
    { score: readinessPillars.profileActivity.score, weight: 5 },
  ];
  const weightSum = parts.reduce((s, x) => s + x.weight, 0);
  const score = Math.round(parts.reduce((s, x) => s + x.score * x.weight, 0) / weightSum);
  return { score: Math.min(100, Math.max(0, score)), itemsRequired, itemsOnFile };
}

/**
 * The same place in a dispatcher job's match: dispatch background (weight 20), experience on the
 * profile (weight 20) and profile basics (weight 5). Never NREMT, a state EMS license or EMS skills.
 */
export function dispatchJobFloor(
  profile: unknown,
  readinessPillars: { experience: { score: number }; profileActivity: { score: number } },
): { score: number; dispatchBackground: boolean } {
  const dispatchBackground = hasDispatchBackground(profile);
  const score = Math.round(((dispatchBackground ? 100 : 0) * 20 + readinessPillars.experience.score * 20 + readinessPillars.profileActivity.score * 5) / 45);
  return { score: Math.min(100, Math.max(0, score)), dispatchBackground };
}
